// Header KPI band — 4 cells: Status · Postęp · Waga · Marża.
//
//   - Status   : lifecycle from tx.computed_status (+ cancelled override)
//   - Postęp   : `*_check`-driven overall progress — counts non-NA items
//                (excludes null + "not_required"); "completed" → done.
//                Matches what `TransactionChecklist` renders row-by-row.
//   - Waga     : effective weight + δ when load+unload both known
//   - Marża    : canonical PLN margin via `shared/calc.computeMarginPLN`
//                (revenue − cost + transport). Per-ton breakdown in sub.
//
// All formatting flows through `useFormat()`; tokens come from `@ds/tokens/palette`.

import React from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import type { Transaction } from "@shared/types/transaction";
import { StatBand, type StatItem, type StatTone } from "@ds/display/StatBand";
import { useFormat } from "@ds/display/useNumberFormat";
import { useCurrentEmployee } from "../../../contexts/useCurrentEmployee";
import { ALL_CHECK_KEYS } from "../transactionChecklist";
import { computeMarginPLN, effectiveWeight } from "@shared/calc";

interface FormatLike {
  weight:   (v: number | null | undefined) => string;
  currency: (v: number | null, ccy: string | null | undefined, decimals?: number) => string;
}

// Status: cancelled (status_override) is the only real transaction status —
// the `computed_status` lifecycle column was dead data and was dropped.

// ─── Checklist progress — same logic as TransactionChecklist.GroupBlock ──
//
// Each check key has a sibling `*_check` column on the transaction record:
//   - `null` or `"not_required"` → excluded from the denominator (NA)
//   - `"completed"`               → done
//   - anything else               → pending
function computeChecklistProgress(tx: Transaction): { done: number; total: number; percent: number } {
  const rec = tx as unknown as Record<string, unknown>;
  let done = 0;
  let total = 0;
  for (const k of ALL_CHECK_KEYS) {
    const state = rec[`${k}_check`];
    if (state == null || state === "not_required") continue;
    total += 1;
    if (state === "completed") done += 1;
  }
  const percent = total > 0 ? Math.round((done / total) * 100) : 100;
  return { done, total, percent };
}

// ─── buildStats — 4-cell band ────────────────────────────────────────────

function buildStats(tx: Transaction, t: TFunction, fmt: FormatLike, showMargin: boolean): StatItem[] {
  const { done, total, percent: pct } = computeChecklistProgress(tx);

  const cancelled = tx.status_override === "cancelled";

  // Weight: show unload (preferred), or load. The sub-line is only useful
  // when there's actual data — we leave it `undefined` rather than spelling
  // "Brak danych" which the user (rightly) found confusing.
  const wValue = tx.unloaded_weight ?? tx.loaded_weight ?? null;
  let wSub: string | undefined;
  if (tx.unloaded_weight != null && tx.loaded_weight != null) {
    const delta = tx.unloaded_weight - tx.loaded_weight;
    wSub = `Δ ${delta >= 0 ? "+" : "−"}${fmt.weight(Math.abs(delta))}`;
  } else if (tx.unloaded_weight == null && tx.loaded_weight != null) {
    wSub = t("transactions.kpi.waiting_unload", "Czeka na rozładunek");
  }

  // Margin: canonical formula from shared/calc (revenue − cost + transport).
  const w = effectiveWeight(tx);
  const marginPLN  = computeMarginPLN(tx);
  const marginText = marginPLN == null
    ? "—"
    : `${marginPLN >= 0 ? "+" : "−"}${fmt.currency(Math.abs(marginPLN), "PLN", 0)}`;
  const marginTone: StatTone = marginPLN == null
    ? "default"
    : marginPLN >= 0 ? "positive" : "negative";
  const marginPerTon = marginPLN != null && w > 0 ? marginPLN / w : null;
  const marginSub = marginPerTon == null
    ? t("transactions.kpi.margin.no_data", "Brak pełnej wyceny")
    : `${marginPerTon >= 0 ? "+" : "−"}${fmt.currency(Math.abs(marginPerTon), "PLN", 2)} / t`;

  const stats: StatItem[] = [
    {
      label: t("transactions.kpi.status", "Status"),
      value: cancelled
        ? t("transactions.lifecycle.cancelled", "Anulowana")
        : t("transactions.lifecycle.active", "Aktywna"),
      sub: cancelled ? t("transactions.lifecycle.sub.cancelled", "Wycofana z obiegu") : undefined,
      valueTone: cancelled ? "negative" : "positive",
    },
    {
      label: t("transactions.kpi.progress", "Postęp"),
      value: `${done}/${total}`,
      sub: `${pct}%`,
      valueTone: pct >= 100 ? "positive" : pct >= 70 ? "default" : "warning",
      progressPct: pct,
    },
    {
      label: t("transactions.kpi.weight", "Waga"),
      value: wValue == null ? "—" : fmt.weight(wValue),
      sub: wSub,
      valueTone: tx.unloaded_weight == null ? "warning" : "default",
    },
  ];
  // Margin is admin-only — non-admin / non-administration roles do not see
  // P&L data in the header.
  if (showMargin) {
    stats.push({
      label: t("transactions.kpi.margin.title", "Marża"),
      value: marginText,
      sub:   marginSub,
      valueTone: marginTone,
    });
  }
  return stats;
}

export interface TransactionKpiBandProps {
  transaction: Transaction;
}

export const TransactionKpiBand: React.FC<TransactionKpiBandProps> = ({ transaction }) => {
  const { t } = useTranslation();
  const fmt = useFormat();
  const { hasRole } = useCurrentEmployee();
  const showMargin = hasRole("admin", "developer");
  return (
    <StatBand
      stats={buildStats(transaction, t, fmt as FormatLike, showMargin)}
      testId="transaction-kpi"
    />
  );
};
